import { useCallback, useEffect, useState } from 'react';
import { AppState } from 'react-native';
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import { FIRESTORE_COLLECTIONS, firestore } from '../firebase';
import {
  AuraType,
  AURA_META,
  calculateAuraScoreForQuestions,
  AuraDimension,
  DAY_VIBES_QUESTIONS,
  getAlignmentLabel,
  Question,
} from '../data/dayVibesQuestions';
import {
  getDayVibeDateKey,
  getNextDayVibeBoundary,
  getNextLocalMidnight,
  isDayVibeResultsOpen,
  isDayVibesOpen,
} from '../utils/dayVibeDate';

export type DayVibesState =
  | 'locked_until_open'
  | 'not_started'
  | 'in_progress'
  | 'submitted_waiting'
  | 'submitted_unlocked';

export interface DayVibesSubmission {
  userId: string;
  date: string;
  dateKey?: string;
  submitted?: boolean;
  answers: Record<string, string[]>;
  selectedAnswers?: Record<string, string[]>;
  scoreBreakdown?: Record<AuraDimension, number>;
  dominantDimensions?: AuraDimension[];
  auraType: AuraType;
  auraName: string;
  auraTraits: string[];
  auraDescription: string;
  aura?: {
    type: AuraType;
    name: string;
    traits: string[];
    description: string;
  };
  createdAt?: unknown;
  expiresAt?: unknown;
  submittedAt: unknown;
  locked: boolean;
}

export interface VibeCircleEntry {
  userId: string;
  displayName: string;
  photoURL: string;
  auraType: AuraType;
  auraName: string;
  alignment: number;
  label: 'instant_sync' | 'strong_sync' | 'recommended_vibe';
  sharedTraits: string[];
  sharedReason: string;
}

const RESPONSES = FIRESTORE_COLLECTIONS.dayVibeResponses;
const MATCHES = FIRESTORE_COLLECTIONS.dayVibeMatches;
const DRAFTS = FIRESTORE_COLLECTIONS.dayVibeDrafts;
const USER_DAY_VIBES = 'dayVibes';

type DayVibeMatchDoc = {
  dateKey?: string;
  users?: string[];
  userA?: string;
  userB?: string;
  matchPercent?: number;
  sameAnswerCount?: number;
  totalCompared?: number;
  status?: string;
  type?: string;
};

// Fired when today's response is written outside this hook (a web Vibe game
// claim, src/features/vibeClaim) so already-mounted Day Vibes screens reload.
const dayVibesChangeListeners = new Set<() => void>();

export function notifyDayVibesChanged(): void {
  dayVibesChangeListeners.forEach(listener => listener());
}

function getTodayKey(): string {
  return getDayVibeDateKey();
}

function isTodaySubmission(data: DayVibesSubmission, dateKey: string): boolean {
  return (data.dateKey ?? data.date) === dateKey;
}

export function useDayVibes(userId: string | null) {
  const [clock, setClock] = useState(() => {
    const now = new Date();
    return { dateKey: getTodayKey(), isOpen: isDayVibesOpen(now), resultsOpen: isDayVibeResultsOpen(now) };
  });
  const [state, setState] = useState<DayVibesState>(clock.isOpen ? 'not_started' : 'locked_until_open');
  const [submission, setSubmission] = useState<DayVibesSubmission | null>(null);
  const [draftAnswers, setDraftAnswers] = useState<Record<string, string[]>>({});
  const [draftQuestionIndex, setDraftQuestionIndex] = useState(0);
  const [vibeCircle, setVibeCircle] = useState<VibeCircleEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const listener = () => setReloadToken(token => token + 1);
    dayVibesChangeListeners.add(listener);
    return () => {
      dayVibesChangeListeners.delete(listener);
    };
  }, []);

  const date = clock.dateKey;
  const isOpen = clock.isOpen;
  const resultsOpen = clock.resultsOpen;

  useEffect(() => {
    const scheduleNextBoundary = () => {
      const now = new Date();
      const nextBoundary = getNextDayVibeBoundary(now);
      const delay = Math.max(1000, nextBoundary.getTime() - now.getTime() + 250);
      return setTimeout(() => {
        const nextNow = new Date();
        setClock({
          dateKey: getDayVibeDateKey(nextNow),
          isOpen: isDayVibesOpen(nextNow),
          resultsOpen: isDayVibeResultsOpen(nextNow),
        });
      }, delay);
    };

    const timer = scheduleNextBoundary();
    return () => clearTimeout(timer);
  }, [clock.dateKey, clock.isOpen, clock.resultsOpen]);

  useEffect(() => {
    const refreshClock = () => {
      const now = new Date();
      setClock({
        dateKey: getDayVibeDateKey(now),
        isOpen: isDayVibesOpen(now),
        resultsOpen: isDayVibeResultsOpen(now),
      });
    };
    const subscription = AppState.addEventListener('change', status => {
      if (status === 'active') refreshClock();
    });
    refreshClock();
    return () => subscription.remove();
  }, []);

  useEffect(() => {
    if (!userId) { setLoading(false); return; }
    const uid = userId;
    let cancelled = false;

    async function load() {
      const responseId = `${uid}_${date}`;
      const draftId = `${uid}_${date}`;
      setLoading(true);
      setSubmission(null);
      setVibeCircle([]);

      if (!isOpen) {
        setDraftAnswers({});
        setDraftQuestionIndex(0);
        setState('locked_until_open');
        setLoading(false);
        return;
      }

      try {
        console.log('[Tonyte][DayVibes] load start', {
          authUid: userId,
          dateKey: date,
          dateStrategy: 'local-yyyy-mm-dd',
          responseCollectionPath: RESPONSES,
          responseDocumentId: responseId,
          draftCollectionPath: DRAFTS,
          draftDocumentId: draftId,
        });

        const userDayVibeRef = doc(firestore, FIRESTORE_COLLECTIONS.users, uid, USER_DAY_VIBES, date);
        const topLevelResponseRef = doc(firestore, RESPONSES, responseId);
        const [userDayVibeSnap, responseSnap] = await Promise.all([
          getDoc(userDayVibeRef).catch(() => null),
          getDoc(topLevelResponseRef),
        ]);

        if (cancelled) return;

        const activeResponseSnap = userDayVibeSnap?.exists() ? userDayVibeSnap : responseSnap;

        if (activeResponseSnap.exists()) {
          const data = activeResponseSnap.data() as DayVibesSubmission;
          if (!isTodaySubmission(data, date)) {
            setSubmission(null);
            setState('not_started');
            return;
          }
          setSubmission(data);
          const resolvedState = resultsOpen ? 'submitted_unlocked' : 'submitted_waiting';
          setState(resolvedState);
          console.log('[Tonyte][DayVibes] response found', {
            collectionPath: RESPONSES,
            documentId: responseId,
            authUid: userId,
            data,
            resolvedState,
          });
          return;
        }

        console.log('[Tonyte][DayVibes] no response found; checking draft', {
          collectionPath: DRAFTS,
          documentId: draftId,
          authUid: userId,
        });

        const draftSnap = await getDoc(doc(firestore, DRAFTS, draftId));

        if (cancelled) return;

        if (draftSnap.exists()) {
          const data = draftSnap.data() as { date?: string; dateKey?: string; answers: Record<string, string[]>; questionIndex: number };
          if ((data.dateKey ?? data.date) !== date) {
            setDraftAnswers({});
            setDraftQuestionIndex(0);
            setState('not_started');
            return;
          }
          setDraftAnswers(data.answers ?? {});
          setDraftQuestionIndex(data.questionIndex ?? 0);
          setState('in_progress');
          console.log('[Tonyte][DayVibes] draft found', {
            collectionPath: DRAFTS,
            documentId: draftId,
            authUid: userId,
            data,
            resolvedState: 'in_progress',
          });
        } else {
          setState('not_started');
          console.log('[Tonyte][DayVibes] no response or draft found', {
            authUid: userId,
            dateKey: date,
            resolvedState: 'not_started',
          });
        }
      } catch (error) {
        console.warn('[Tonyte][DayVibes] load failed', {
          authUid: userId,
          dateKey: date,
          responseCollectionPath: RESPONSES,
          responseDocumentId: responseId,
          draftCollectionPath: DRAFTS,
          draftDocumentId: draftId,
          error,
        });
        setState('not_started');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [userId, date, isOpen, resultsOpen, reloadToken]);

  const saveDraft = useCallback(async (
    answers: Record<string, string[]>,
    questionIndex: number,
  ) => {
    if (!userId) return;
    if (!isOpen) return;
    const draftId = `${userId}_${date}`;
    try {
      console.log('[Tonyte][DayVibes] draft save start', {
        collectionPath: DRAFTS,
        documentId: draftId,
        authUid: userId,
        dateKey: date,
        questionIndex,
      });
      await setDoc(doc(firestore, DRAFTS, draftId), {
        userId,
        date,
        dateKey: date,
        answers,
        questionIndex,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setDraftAnswers(answers);
      setDraftQuestionIndex(questionIndex);
      if (state === 'not_started') setState('in_progress');
    } catch (error) {
      console.warn('[Tonyte][DayVibes] draft save failed', { userId, date, error });
    }
  }, [userId, date, isOpen, state]);

  const submitAnswers = useCallback(async (
    answers: Record<string, string[]>,
    questionsForAura?: Question[],
  ): Promise<DayVibesSubmission | null> => {
    if (!userId) return null;
    if (!isOpen) return null;
    const responseId = `${userId}_${date}`;
    const draftId = `${userId}_${date}`;
    const expiresAtLocal = getNextLocalMidnight();
    const userDayVibeRef = doc(firestore, FIRESTORE_COLLECTIONS.users, userId, USER_DAY_VIBES, date);
    const topLevelResponseRef = doc(firestore, RESPONSES, responseId);

    console.log('[Tonyte][DayVibes] submit start', {
      responseCollectionPath: RESPONSES,
      responseDocumentId: responseId,
      draftCollectionPath: DRAFTS,
      draftDocumentId: draftId,
      authUid: userId,
      dateKey: date,
    });

    const scoringQuestions = questionsForAura?.length ? questionsForAura : DAY_VIBES_QUESTIONS;
    const auraResult = calculateAuraScoreForQuestions(scoringQuestions, answers);
    const auraType = auraResult.auraType;
    const scoreBreakdown = auraResult.scoreBreakdown;
    const dominantDimensions = auraResult.dominantDimensions;
    const auraMeta = AURA_META[auraType];
    const submittedAtLocal = new Date().toISOString();

    // Payload for Firestore: serverTimestamp() for submittedAt
    const firestorePayload = {
      userId,
      date,
      dateKey: date,
      submitted: true,
      answers,
      selectedAnswers: answers,
      scoreBreakdown,
      dominantDimensions,
      auraType,
      auraName: auraMeta.name,
      auraTraits: auraMeta.traits,
      auraDescription: auraMeta.description,
      aura: {
        type: auraType,
        name: auraMeta.name,
        traits: auraMeta.traits,
        description: auraMeta.description,
      },
      createdAt: serverTimestamp(),
      submittedAt: serverTimestamp(),
      expiresAt: expiresAtLocal,
      locked: true,
      // Server matching status (functions/index.js): queued → matching →
      // complete | retrying. Lets the Vibe Circle tell "still matching" from
      // "no one compatible yet" from the very first second.
      matchingStatus: 'queued',
    };

    // Local state representation: plain ISO string (no Firestore sentinel)
    const localSubmission: DayVibesSubmission = {
      userId,
      date,
      dateKey: date,
      submitted: true,
      answers,
      selectedAnswers: answers,
      scoreBreakdown,
      dominantDimensions,
      auraType,
      auraName: auraMeta.name,
      auraTraits: auraMeta.traits,
      auraDescription: auraMeta.description,
      aura: {
        type: auraType,
        name: auraMeta.name,
        traits: auraMeta.traits,
        description: auraMeta.description,
      },
      createdAt: submittedAtLocal,
      submittedAt: submittedAtLocal,
      expiresAt: expiresAtLocal.toISOString(),
      locked: true,
    };

    console.log('[Tonyte][DayVibes] response payload', {
      collectionPath: RESPONSES,
      documentId: responseId,
      authUid: userId,
      dateKey: date,
      auraType,
      locked: true,
    });

    try {
      const [existingUserSnap, existingTopLevelSnap] = await Promise.all([
        getDoc(userDayVibeRef).catch(() => null),
        getDoc(topLevelResponseRef),
      ]);
      const existingSnap = existingUserSnap?.exists() ? existingUserSnap : existingTopLevelSnap;
      if (existingSnap.exists()) {
        const existing = existingSnap.data() as DayVibesSubmission;
        const resolvedState = isTodaySubmission(existing, date)
          ? resultsOpen ? 'submitted_unlocked' : 'submitted_waiting'
          : 'not_started';
        console.log('[Tonyte][DayVibes] submit skipped; existing response found', {
          collectionPath: RESPONSES,
          documentId: responseId,
          authUid: userId,
          data: existing,
          resolvedState,
        });
        setSubmission(isTodaySubmission(existing, date) ? existing : null);
        setState(resolvedState);
        return isTodaySubmission(existing, date) ? existing : null;
      }

      await setDoc(topLevelResponseRef, firestorePayload);
      console.log('[Tonyte][DayVibes] response write success', { responseId });

      setDoc(userDayVibeRef, firestorePayload).catch(mirrorErr => {
        console.warn('[Tonyte][DayVibes] user dayVibes mirror write failed (non-fatal)', {
          userId,
          date,
          mirrorErr,
        });
      });

      try {
        await deleteDoc(doc(firestore, DRAFTS, draftId));
        console.log('[Tonyte][DayVibes] draft deleted', { draftId });
      } catch (draftErr) {
        console.warn('[Tonyte][DayVibes] draft delete failed (non-fatal)', { draftId, draftErr });
      }

      setSubmission(localSubmission);
      setState(resultsOpen ? 'submitted_unlocked' : 'submitted_waiting');
      return localSubmission;
    } catch (error) {
      console.error('[Tonyte][DayVibes] submit failed', { userId, date, error });
      return null;
    }
  }, [userId, date, isOpen, resultsOpen]);

  const loadVibeCircle = useCallback(async () => {
    if (!userId || !submission) return;
    try {
      const q = query(
        collection(firestore, MATCHES),
        where('users', 'array-contains', userId),
        where('dateKey', '==', date),
        orderBy('matchPercent', 'desc'),
        limit(15),
      );
      console.log('[Tonyte][DayVibes] vibe circle query', {
        collectionPath: MATCHES,
        authUid: userId,
        dateKey: date,
        where: [
          ['users', 'array-contains', userId],
          ['dateKey', '==', date],
        ],
        orderBy: ['matchPercent', 'desc'],
      });
      const snap = await getDocs(q);
      const results = await Promise.all(snap.docs.map(async docSnap => {
        const data = docSnap.data() as DayVibeMatchDoc;
        if (data.dateKey !== date) return null;
        const otherUserId = data.userA === userId
          ? data.userB
          : data.userB === userId
          ? data.userA
          : Array.isArray(data.users)
          ? data.users.find(id => id !== userId)
          : undefined;
        if (!otherUserId) return null;

        // No usable score → no entry (never a stand-in percentage).
        if (typeof data.matchPercent !== 'number' || !Number.isFinite(data.matchPercent)) return null;
        const pct = data.matchPercent;
        const label = getAlignmentLabel(pct);
        if (!label) return null;

        const [profileSnap, auraSnap] = await Promise.all([
          getDoc(doc(firestore, FIRESTORE_COLLECTIONS.users, otherUserId)).catch(() => null),
          getDoc(doc(firestore, FIRESTORE_COLLECTIONS.users, otherUserId, USER_DAY_VIBES, date)).catch(() => null),
        ]);
        const profile = profileSnap?.data() as
          | { name?: string; displayName?: string; photo?: string; profilePhotoURL?: string; profileImage?: string }
          | undefined;
        const auraData = auraSnap?.data() as
          | { auraType?: AuraType; auraName?: string; date?: string; dateKey?: string; submitted?: boolean }
          | undefined;
        const hasTodayAura =
          auraData &&
          (auraData.dateKey ?? auraData.date) === date &&
          auraData.submitted !== false &&
          auraData.auraType &&
          AURA_META[auraData.auraType];
        const auraType = hasTodayAura ? auraData.auraType as AuraType : 'CITY_SOUL';
        const auraMeta = AURA_META[auraType];

        const sharedTraits = auraMeta.traits.filter(t =>
          AURA_META[submission.auraType].traits.includes(t),
        );

        return {
          userId: otherUserId,
          displayName: profile?.displayName ?? profile?.name ?? 'Traveler',
          photoURL: profile?.profilePhotoURL ?? profile?.profileImage ?? profile?.photo ?? '',
          auraType,
          auraName: hasTodayAura ? auraData.auraName ?? auraMeta.name : auraMeta.name,
          alignment: pct,
          label,
          sharedTraits: sharedTraits.length > 0 ? sharedTraits : auraMeta.traits.slice(0, 2),
          sharedReason: `Your vibe alignment is ${Math.min(pct, 99)}%.`,
        } satisfies VibeCircleEntry;
      }));

      setVibeCircle(results.filter(Boolean) as VibeCircleEntry[]);
    } catch (error) {
      console.warn('[Tonyte][DayVibes] vibe circle load failed', { userId, date, error });
    }
  }, [userId, date, submission]);

  // Auto-load vibe circle when state unlocks
  useEffect(() => {
    if (state === 'submitted_unlocked' && submission && vibeCircle.length === 0) {
      loadVibeCircle();
    }
  }, [state, submission, vibeCircle.length, loadVibeCircle]);

  return {
    state,
    submission,
    draftAnswers,
    draftQuestionIndex,
    vibeCircle,
    loading,
    dateKey: date,
    isOpen,
    resultsOpen,
    saveDraft,
    submitAnswers,
    loadVibeCircle,
  };
}
